import { TableIds } from "../../../common/table-identifier-utils"
import {
    checkboxColDefCreator,
    companyStartStopTypeColDefCreator,
    dateColDefCreator,
    datetimeColDefCreator,
    editableTimeCardColDefCreator,
    employeeColDefCreator,
    lengthLimitedColDefCreator,
    stringColDefCreator,
    timekeepingStatusColDefCreator,
} from "./standard-col-def-creators"
import { alertCellClassRule, getAvailableStatusValues, isGroupRow, timekeepingDataIsEditable, timekeepingRowIsEditable } from "../../../common/ag-grid-utils"
import GroupRowFormattingDefaultCellRenderer from "../cell-renderers/GroupRowFormattingDefaultCellRenderer"
import { getSelectorActionButtons } from "./action-button-settings"
import { getFlagEnabled } from "../../../getFlagValue"
import { getShiftNodeForEmployee, findShiftRowData, getObjectId, lockedPeriodCellClassRule } from "../../../common/ag-grid-ts-utils"
import { addDays, addMinutes, differenceInMinutes, parseISO } from "date-fns"
import { simpleDateTimeValueFormatter, simpleTimeValueFormatter } from "../../../common/ag-grid-value-formatters"
import { compareGroupColumns } from "../../../common/ag-grid-comparators"
import { timekeepingCommentTooltip, timekeepingEntryGeneralErrorsTooltip } from "../../../common/ag-grid-custom-tooltip-formatters"
import { weeklyTkDummyProject } from "../../../common/constants"
import { toggleCicoVisible, togglePlaceholdersVisible } from "../../../actions"
import { convertDecimalHoursToHoursAndMinutes, getAsDate, isDateTheNextDay } from "../../../common/ts-utils"
import { colorAttentionBlue50, colorAttentionRed60, colorFunctionalGray50 } from "@rhumbix/rmbx_design_system_web"
import { cicoEnabledFromLockedColumns } from "../utils"
import { addEmployeeRowsTableAction, addRowTableAction, editTimecardDetailsAction, viewCicoTableButton, viewCicoForSelectedRowsButton } from "./common-button-defs"
import { CellClassParams, ICellEditorParams, IRowNode, ValueFormatterParams, ValueGetterParams } from "ag-grid-community"
import { ExceptionsState, iCurrentUser, tFeatures } from "../../../common/types"
import { tProject } from "../../../cached-data/types"
import { tContext } from "../types"
import { tSourceData } from "../../../dashboard-data/types"
import { toggleException } from "../../../actions/exceptions"
import { activateException } from "../custom-filters/utils"
import { Dispatch } from "react"

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const getLocalWorkshiftDate = (params: ICellEditorParams) => {
    let date = new Date(params.data?.date)
    date = addMinutes(date, date.getTimezoneOffset())
    return date
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const getLocalWorkshiftStartOrStop = (params: ICellEditorParams) => {
    if (params.data?.company_start_stop_type?.is_break === false) {
        if (params.colDef.field === "/start_time") return getAsDate(params.data.shift_start_time)
        else if (params.colDef.field === "/stop_time") return getAsDate(params.data.shift_end_time)
    }
    return null
}

/**
 * Formats a shift/break start or stop time, including the date when WA-9559-fix-overnight-date-picker
 * is on so that times spanning midnight aren't ambiguous.
 * @param params Input data.
 * @param params.value The start or stop time to format.
 * @returns The formatted time, with the date included when the flag is on.
 */
const shiftTimeValueFormatter = (params: { value: string }): string => (getFlagEnabled("WA-9559-fix-overnight-date-picker") ? simpleDateTimeValueFormatter(params) : simpleTimeValueFormatter(params))

const allExceptionsToggle = (context: tContext) => {
    const exceptionTypes = ["workRulesException"]
    const allOn = exceptionTypes.every(et => (context.exceptions[et as keyof ExceptionsState] as { enabled: boolean }).enabled)
    const allOff = exceptionTypes.every(et => !(context.exceptions[et as keyof ExceptionsState] as { enabled: boolean }).enabled)
    const isMixed = !allOn && !allOff
    const desiredState = !!(isMixed || allOff)
    return (dispatch: Dispatch<any>) => {
        if (context.exceptions.workRulesException.enabled !== desiredState) dispatch(toggleException("workRulesException"))
    }
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getShiftsAndBreaksTable = (featureFlags: tFeatures, currentProject: tProject, user: iCurrentUser) => {
    const userRole = user ? user.user_role : undefined
    const parentContainerId = { parentContainerId: "weekly-tk-detail-table-modal" }
    return {
        tableName: "Shifts and Breaks",
        id: TableIds.ShiftsAndBreaksModal,
        navId: "shifts-and-breaks",
        resources: ["ewsStartStopTimes"],
        cicoResource: "timelineEntryVersions",
        colDefs: [
            dateColDefCreator({}),
            {
                ...checkboxColDefCreator({}),
                checkboxSelection: (p: IRowNode) => !isGroupRow(p),
            },
            {
                ...employeeColDefCreator({
                    cellRenderer: GroupRowFormattingDefaultCellRenderer,
                }),
                ignoreRelatedFilters: ["projectId"],
            },
            {
                ...editableTimeCardColDefCreator({
                    headerName: "Time Card",
                    required: true,
                    width: 300,
                    features: featureFlags,
                }),
                ignoreRelatedFilters: ["projectId"],
                extraFilterQueryParams: ["start_date", "foreman_id"],
                extraSearchFilters: { allow_empty: true },
                actionOptions: [getSelectorActionButtons().createNewTimecardButton],
            },
            companyStartStopTypeColDefCreator({
                required: true,
                cellRenderer: GroupRowFormattingDefaultCellRenderer,
            }),
            {
                ...datetimeColDefCreator({
                    cellEditorParams: {
                        minuteInterval: 1,
                        getOpenToDate: getLocalWorkshiftDate,
                        getMinDate: getLocalWorkshiftDate,
                        getMaxDate: (params: ICellEditorParams) => {
                            const localWorkshiftDate = getLocalWorkshiftDate(params)
                            const isShift = params.data?.is_break === false || params.data?.company_start_stop_type?.is_break === false

                            // Shifts must start on the same day as the shift date (in local time)
                            if (isShift) {
                                return localWorkshiftDate
                            }

                            // Breaks/meals can start on the next day if the shift ends on the next day
                            const shiftData = getShiftNodeForEmployee(params.api, params.data)
                            if (shiftData?.stop_time) {
                                const shiftEntryEndTime = parseISO(shiftData.stop_time)
                                return isDateTheNextDay(localWorkshiftDate, shiftEntryEndTime) ? addDays(localWorkshiftDate, 1) : localWorkshiftDate
                            }

                            // Otherwise, breaks/meals must start on the same day
                            return localWorkshiftDate
                        },
                        getInitialValue: getLocalWorkshiftStartOrStop,
                        stopEditAfterSelectTime: true,
                    },
                    cellRenderer: GroupRowFormattingDefaultCellRenderer,
                    field: "/start_time",
                    headerName: "Start Time",
                    required: true,
                    valueFormatter: (params: ValueFormatterParams) => {
                        if (isGroupRow(params) && params.node) {
                            const shift = findShiftRowData(params.node)
                            if (!shift) return ""
                            return shiftTimeValueFormatter({
                                value: shift.start_time,
                            })
                        }
                        return shiftTimeValueFormatter(params)
                    },
                }),
                minWidth: 320,
            },
            {
                ...datetimeColDefCreator({
                    cellEditorParams: {
                        minuteInterval: 1,
                        getOpenToDate: getLocalWorkshiftDate,
                        getMinDate: getLocalWorkshiftDate,
                        getMaxDate: (params: ICellEditorParams) => {
                            const localWorkshiftDate = getLocalWorkshiftDate(params)

                            if (featureFlags.disable_work_shift_start_stop_times && getFlagEnabled("WA-9559-fix-overnight-date-picker")) {
                                // Shifts and breaks/meals can all end on the next day, regardless of type,
                                // since the work shift's own start/stop time isn't available to bound this to a single day
                                return addDays(localWorkshiftDate, 1)
                            }

                            const isShift = params.data?.is_break === false || params.data?.company_start_stop_type?.is_break === false
                            // Shifts can always end on the next day
                            if (isShift) {
                                return addDays(localWorkshiftDate, 1)
                            }

                            // Breaks/meals can end on the next day if the shift ends on the next day
                            const shiftData = getShiftNodeForEmployee(params.api, params.data)
                            if (shiftData?.stop_time) {
                                const shiftEntryEndTime = parseISO(shiftData.stop_time)
                                return isDateTheNextDay(localWorkshiftDate, shiftEntryEndTime) ? addDays(localWorkshiftDate, 1) : localWorkshiftDate
                            }

                            // Otherwise, breaks/meals must end on the same day
                            return localWorkshiftDate
                        },
                        getInitialValue: getLocalWorkshiftStartOrStop,
                        stopEditAfterSelectTime: true,
                    },
                    cellRenderer: GroupRowFormattingDefaultCellRenderer,
                    field: "/stop_time",
                    headerName: "Stop Time",
                    required: true,
                    valueFormatter: (params: ValueFormatterParams) => {
                        if (isGroupRow(params) && params.node) {
                            const shift = findShiftRowData(params.node)
                            if (!shift) return ""
                            return shiftTimeValueFormatter({
                                value: shift.stop_time,
                            })
                        }
                        return shiftTimeValueFormatter(params)
                    },
                }),
                minWidth: 320,
            },
            {
                ...stringColDefCreator({
                    cellRenderer: GroupRowFormattingDefaultCellRenderer,
                    editable: false,
                    headerName: "Duration",
                    valueGetter: (params: ValueGetterParams) => {
                        let { data } = params
                        if (isGroupRow(params) && params.node) {
                            const shift = findShiftRowData(params.node)
                            data = shift
                        }
                        if (!data) return

                        const { is_break, start_time, stop_time } = data
                        if (!start_time || !stop_time) {
                            return ""
                        }

                        const durationInMinutes = differenceInMinutes(new Date(stop_time), new Date(start_time))

                        if (is_break) {
                            return `${durationInMinutes} minutes`
                        }

                        const shiftHours = Math.floor(durationInMinutes / 60)
                        const shiftMinutes = durationInMinutes % 60
                        let shiftDurationText = ""
                        if (shiftHours) {
                            shiftDurationText += `${shiftHours} hours`
                        }
                        if (shiftMinutes) {
                            shiftDurationText += `${shiftHours ? ", " : ""}${shiftMinutes} minutes`
                        }

                        return shiftDurationText
                    },
                }),
            },
            // This value gets computed on the fly
            ...(featureFlags.cico
                ? [
                      {
                          headerName: "CI/CO",
                          field: "/cico_diff",
                          minWidth: 100,
                          editable: false,
                          cellStyle: (params: CellClassParams) => {
                              if (params.value === "--") return { color: colorFunctionalGray50 }
                              const val = parseFloat(params.value)
                              if (val > 0) return { color: colorAttentionBlue50 }
                              else if (val < 0) return { color: colorAttentionRed60 }
                          },
                          valueGetter: (params: ValueGetterParams) => {
                              const cicoEnabled = cicoEnabledFromLockedColumns(params.context)
                              const { referenceableData } = params.context
                              let timecardCicoEnabled = true
                              if (params.data.work_shift_id) {
                                  const workShift = referenceableData.workShifts[getObjectId(params.data.work_shift_id)]

                                  timecardCicoEnabled = workShift?.projects?.some((pId: number) => referenceableData.projects[pId]?.cico_settings?.enabled)
                              }
                              return cicoEnabled && timecardCicoEnabled ? convertDecimalHoursToHoursAndMinutes(params.data.cico_diff) : "--"
                          },
                      },
                  ]
                : []),
            ...(featureFlags.timekeeping_comments
                ? [
                      {
                          ...lengthLimitedColDefCreator({
                              headerName: "Comment",
                              field: "/comment",
                              maxLength: user.company_options.tkcomment_max_length,
                          }),
                          customTooltip: timekeepingCommentTooltip,
                      },
                  ]
                : []),
            {
                ...timekeepingStatusColDefCreator({
                    enum: getAvailableStatusValues(userRole),
                }),
                cellRenderer: GroupRowFormattingDefaultCellRenderer,
                aggFunc: "statusColumnAggFunc",
            },
        ],
        gridSettings: {
            autoGroupColumnDef: {
                headerName: "Type",
                suppressMenu: true,
                sort: "asc",
                unSortIcon: true,
                cellRendererParams: {
                    suppressCount: true,
                },
                comparator: compareGroupColumns,
                checkboxSelection: (p: IRowNode) => isGroupRow(p),
                groupSelectsChildren: false,
                minWidth: 250,
            },
            defaultColDef: {
                cellClassRules: {
                    "alert-cell": alertCellClassRule,
                    "period-locked": lockedPeriodCellClassRule,
                    readonly: (params: CellClassParams) => !timekeepingDataIsEditable(params).canEdit,
                },
                editable: (params: CellClassParams) => timekeepingDataIsEditable(params).canEdit,
                filter: false,
                customTooltip: (params: CellClassParams) => timekeepingEntryGeneralErrorsTooltip(params, true),
                warningsAndErrorsTooltip: true,
                ...parentContainerId,
            },
            enableRangeSelection: true,
            rowSelection: "multiple",
            suppressRowClickSelection: true,
            suppressMultiRangeSelection: true,
            isRowSelectable: (rowNode: IRowNode) => timekeepingRowIsEditable(rowNode),
        },
        otherSettings: {
            pasteRangeEnabled: true,
            enableClipboard: true,
            filterByLockedColumns: true,
            hideLockedColumns: true,
            highlightErrorRows: true,
            isInModalTab: true,
            sizeColumnsToFitSize: 915,
            rowLevelErrorDisplay: true,
            suppressExportButton: true,
            exceptionFilters: ["workRulesException"],
            hiddenColumnDefaults: {
                project: {
                    value: weeklyTkDummyProject,
                },
            },
            groupBy: [
                {
                    cols: [],
                    label: "None",
                },
                {
                    cols: ["/employee"],
                    label: "Employee",
                },
            ],
            buttons: {
                cell: [],
                row: [
                    {
                        label: "Delete",
                        icon: "delete",
                        action: "deleteSelectedRows",
                        args: {
                            local_state_update: true,
                        },
                    },
                    editTimecardDetailsAction,
                    ...(featureFlags.cico ? [viewCicoForSelectedRowsButton] : []),
                ],
                groupRow: [
                    {
                        label: "Add Row",
                        icon: "add",
                        action: "addNewRowToGroup",
                    },
                ],
                table: [
                    {
                        label: "Group By",
                        action: "groupBy",
                        icon: "group",
                    },
                    addRowTableAction(featureFlags),
                    addEmployeeRowsTableAction,
                    editTimecardDetailsAction,
                    ...(featureFlags.cico
                        ? [
                              {
                                  label: "Show/Hide ▾",
                                  icon: "view",
                                  args: {
                                      extraArgs: {
                                          options: [
                                              {
                                                  label: "Placeholders",
                                                  action: togglePlaceholdersVisible,
                                                  // The state parameter we look at to see if this should be
                                                  // checked on
                                                  activeStateParams: ["placeholdersVisible"],
                                              },
                                              {
                                                  label: "CI/CO Data",
                                                  action: toggleCicoVisible,
                                                  // The state parameter we look at to see if this should be
                                                  // checked on
                                                  activeStateParams: ["cicoVisible"],
                                                  disabled: (ctx: tContext) => !cicoEnabledFromLockedColumns(ctx),
                                              },
                                          ],
                                      },
                                  },
                                  checklist: true,
                              },
                              viewCicoTableButton,
                              /* eslint-disable @typescript-eslint/explicit-module-boundary-types */
                              {
                                  label: "Import From CI/CO",
                                  icon: "import",
                                  action: "importShiftsAndBreaksFromCico",
                                  disabled: (_: tContext, sourceData: tSourceData) => {
                                      return !!sourceData.ewsStartStopTimes?.filter(r => !r.is_cico_row).length
                                  },
                                  tooltip: (_: tContext, sourceData: tSourceData) => {
                                      return sourceData.ewsStartStopTimes?.filter(r => !r.is_cico_row).length
                                          ? "You must remove all existing Shifts and Breaks \
                                                    before importing from CI/CO"
                                          : "Import Shifts and Breaks from CI/CO data"
                                  },
                              },
                              /* eslint-enable @typescript-eslint/explicit-module-boundary-types */
                              {
                                  label: "Exceptions ▾",
                                  icon: "exception",
                                  args: {
                                      extraArgs: {
                                          options: [
                                              {
                                                  label: "Exceptions",
                                                  action: allExceptionsToggle,
                                                  exceptionParams: ["workRulesException"],
                                                  hasGear: false,
                                              },
                                              {
                                                  label: "Work Rules",
                                                  action: activateException(() => toggleException("workRulesException")),
                                                  exceptionParams: ["workRulesException"],
                                                  hasGear: true,
                                                  gearModalType: "workRulesException",
                                                  isChild: true,
                                              },
                                          ],
                                          filterToggle: true,
                                          exceptionList: true,
                                      },
                                  },
                                  checklist: true,
                              },
                          ]
                        : []),
                ],
            },
        },
    }
}
